import { useState } from 'react';
import { Link } from 'react-router-dom';
import RegentsTestCard from './components/RegentsTestCard';
import { regentsTest1 } from './data/regentsTest1';
import { regentsTest2 } from './data/regentsTest2';
import { regentsTest3 } from './data/regentsTest3';
import { regentsTest4 } from './data/regentsTest4';
import { regentsTest5 } from './data/regentsTest5';
import type { RegentsQuestion } from './data/regentsTest1';

const tests = [
  {
    number: 1,
    title: 'Practice Regents Test 1',
    subtitle: 'Linear Equations, Polynomials, Functions & Quadratics',
    topics: ['Linear Equations', 'Polynomials', 'Factoring', 'Systems', 'Quadratic Formula', 'Functions', 'Statistics'],
    questions: regentsTest1,
    color: 'from-teal-500 to-emerald-600',
    icon: 'ri-file-list-3-fill',
    difficulty: 'Foundational',
    difficultyColor: 'bg-emerald-100 text-emerald-700',
  },
  {
    number: 2,
    title: 'Practice Regents Test 2',
    subtitle: 'Slope, Factoring, Exponential Functions & Statistics',
    topics: ['Slope-Intercept Form', 'Factoring Trinomials', 'Exponential Decay', 'Vertex Form', 'Geometric Sequences', 'Residuals'],
    questions: regentsTest2,
    color: 'from-emerald-500 to-cyan-600',
    icon: 'ri-file-list-3-fill',
    difficulty: 'Intermediate',
    difficultyColor: 'bg-cyan-100 text-cyan-700',
  },
  {
    number: 3,
    title: 'Practice Regents Test 3',
    subtitle: 'Completing the Square, Systems, Exponential Models & Data',
    topics: ['Completing the Square', 'Elimination Method', 'Discriminant', 'Exponential Growth', 'Scatter Plots', 'Two-Way Tables'],
    questions: regentsTest3,
    color: 'from-cyan-500 to-teal-600',
    icon: 'ri-file-list-3-fill',
    difficulty: 'Intermediate',
    difficultyColor: 'bg-teal-100 text-teal-700',
  },
  {
    number: 4,
    title: 'Practice Regents Test 4',
    subtitle: 'Quadratic Formula, Compound Inequalities, Transformations & Modeling',
    topics: ['Quadratic Formula', 'Compound Inequalities', 'Transformations', 'Literal Equations', 'Geometric Sequences', 'Standard Deviation'],
    questions: regentsTest4,
    color: 'from-teal-600 to-emerald-500',
    icon: 'ri-file-list-3-fill',
    difficulty: 'Advanced',
    difficultyColor: 'bg-amber-100 text-amber-700',
  },
  {
    number: 5,
    title: 'Practice Regents Test 5',
    subtitle: 'Comprehensive Review — All Algebra 1 Topics',
    topics: ['All Topics', 'Word Problems', 'Function Composition', 'Systems Applications', 'Exponential Models', 'Data Analysis'],
    questions: regentsTest5,
    color: 'from-emerald-600 to-teal-500',
    icon: 'ri-file-list-3-fill',
    difficulty: 'Comprehensive',
    difficultyColor: 'bg-rose-100 text-rose-700',
  },
];

export default function AlgebraRegentsPage() {
  const [activeTest, setActiveTest] = useState<{ number: number; questions: RegentsQuestion[] } | null>(null);

  if (activeTest) {
    return (
      <RegentsTestCard
        testNumber={activeTest.number}
        questions={activeTest.questions}
        onClose={() => setActiveTest(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                <i className="ri-graduation-cap-fill text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Mr. Augustine's Math Academy</h1>
                <p className="text-xs text-teal-600 font-medium">Algebra 1 — Regents Practice Tests</p>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/grades/grade-9"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm whitespace-nowrap"
              >
                <i className="ri-book-open-line mr-2"></i>Algebra 1 Lessons
              </Link>
              <Link
                to="/grades"
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>All Grades
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-teal-700 via-emerald-700 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-4">
                <i className="ri-award-fill text-amber-300"></i>
                New York State Regents Examination — Algebra 1
              </div>
              <h2 className="text-4xl font-black mb-4 leading-tight">
                5 Full Practice<br />
                <span className="text-amber-300">Regents Tests</span>
              </h2>
              <p className="text-teal-100 text-lg leading-relaxed mb-6 max-w-xl">
                Authentic Regents-style questions covering every Algebra 1 topic. Each test has 30 questions with complete step-by-step solutions — just like the real exam.
              </p>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 bg-white/15 px-4 py-2 rounded-full text-sm font-medium">
                  <i className="ri-question-line text-amber-300"></i>
                  150 Total Questions
                </div>
                <div className="flex items-center gap-2 bg-white/15 px-4 py-2 rounded-full text-sm font-medium">
                  <i className="ri-lightbulb-line text-amber-300"></i>
                  Full Solutions Included
                </div>
                <div className="flex items-center gap-2 bg-white/15 px-4 py-2 rounded-full text-sm font-medium">
                  <i className="ri-bar-chart-line text-amber-300"></i>
                  Instant Score & Feedback
                </div>
              </div>
            </div>
            <div className="flex-shrink-0">
              <div className="w-48 h-48 bg-white/10 rounded-3xl flex flex-col items-center justify-center border border-white/20 shadow-2xl">
                <i className="ri-file-list-3-fill text-6xl text-amber-300 mb-2"></i>
                <div className="text-3xl font-black">5</div>
                <div className="text-teal-200 text-sm font-medium">Practice Tests</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NEW: Open-Ended Regents Exam Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/grades/algebra-regents-open"
          className="block bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group"
        >
          <div className="p-8 flex flex-col md:flex-row items-center gap-6">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <i className="ri-file-list-3-line text-4xl text-white"></i>
            </div>
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-xs font-bold mb-3">
                <i className="ri-star-fill text-amber-300"></i>
                FULL NY REGENTS EXAM
              </div>
              <h3 className="text-2xl font-black text-white mb-2">
                Take the Complete NY Algebra 1 Regents Exam
              </h3>
              <p className="text-indigo-100 text-sm leading-relaxed max-w-2xl">
                Authentic 4-part exam with 43 questions (98 points total): Multiple choice, short answer, and extended response questions. 
                Includes detailed sample answers, scoring rubrics, and common mistakes for every open-ended question.
              </p>
              <div className="flex flex-wrap gap-3 mt-4 justify-center md:justify-start">
                <span className="px-3 py-1 bg-white/15 rounded-full text-xs font-medium">
                  <i className="ri-file-list-line mr-1"></i>Part I: 30 MC
                </span>
                <span className="px-3 py-1 bg-white/15 rounded-full text-xs font-medium">
                  <i className="ri-edit-line mr-1"></i>Part II: 8 Short Answer
                </span>
                <span className="px-3 py-1 bg-white/15 rounded-full text-xs font-medium">
                  <i className="ri-file-text-line mr-1"></i>Part III: 4 Extended
                </span>
                <span className="px-3 py-1 bg-white/15 rounded-full text-xs font-medium">
                  <i className="ri-star-line mr-1"></i>Part IV: 1 Comprehensive
                </span>
              </div>
            </div>
            <div className="flex-shrink-0">
              <div className="px-8 py-4 bg-white text-indigo-600 rounded-xl font-bold text-sm shadow-lg group-hover:bg-indigo-50 transition-colors whitespace-nowrap">
                Start Full Exam
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* NEW: Study Plan Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <Link
          to="/grades/algebra-regents-study-plan"
          className="block bg-gradient-to-r from-rose-500 via-pink-500 to-fuchsia-500 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group"
        >
          <div className="p-8 flex flex-col md:flex-row items-center gap-6">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <i className="ri-calendar-check-line text-4xl text-white"></i>
            </div>
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-xs font-bold mb-3">
                <i className="ri-roadmap-line text-amber-300"></i>
                PERSONALIZED STUDY PLAN
              </div>
              <h3 className="text-2xl font-black text-white mb-2">
                Get Your Week-by-Week Study Timeline
              </h3>
              <p className="text-rose-100 text-sm leading-relaxed max-w-2xl">
                Choose from 3 study tracks (8-week, 12-week, or 16-week) with a complete roadmap showing exactly what to study each week. 
                Includes a quiz to recommend the perfect plan based on your schedule and comfort level.
              </p>
              <div className="flex flex-wrap gap-3 mt-4 justify-center md:justify-start">
                <span className="px-3 py-1 bg-white/15 rounded-full text-xs font-medium">
                  <i className="ri-time-line mr-1"></i>3 Study Tracks
                </span>
                <span className="px-3 py-1 bg-white/15 rounded-full text-xs font-medium">
                  <i className="ri-book-open-line mr-1"></i>Weekly Topics
                </span>
                <span className="px-3 py-1 bg-white/15 rounded-full text-xs font-medium">
                  <i className="ri-questionnaire-line mr-1"></i>Personalized Quiz
                </span>
                <span className="px-3 py-1 bg-white/15 rounded-full text-xs font-medium">
                  <i className="ri-bar-chart-line mr-1"></i>Progress Tracker
                </span>
              </div>
            </div>
            <div className="flex-shrink-0">
              <div className="px-8 py-4 bg-white text-rose-600 rounded-xl font-bold text-sm shadow-lg group-hover:bg-rose-50 transition-colors whitespace-nowrap">
                View Study Plans
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Teacher Message */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 flex items-start gap-5">
          <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-user-smile-fill text-white text-2xl"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">A Message from Mr. Augustine</h3>
            <p className="text-gray-600 leading-relaxed text-sm">
              "These practice tests are designed to mirror the actual New York State Algebra 1 Regents Examination. 
              Each question targets a specific standard — the same standards tested on the real Regents. 
              Take each test under timed conditions (90 minutes), then review every solution carefully. 
              Understanding <em>why</em> an answer is correct is more valuable than just getting it right. 
              You have everything it takes to pass — and to excel!"
            </p>
            <p className="text-teal-600 font-bold text-sm mt-2">— Mr. Augustine Anyimadu-Ahenkae, Former NYC Public High School Math Teacher</p>
          </div>
        </div>
      </div>

      {/* Standards Coverage */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-6 text-white">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <i className="ri-list-check-2 text-amber-300"></i>
            Standards Covered Across All 5 Tests
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Number & Quantity', codes: 'N-RN, N-Q', icon: 'ri-number-1' },
              { label: 'Algebra', codes: 'A-SSE, A-APR, A-CED, A-REI', icon: 'ri-function-line' },
              { label: 'Functions', codes: 'F-IF, F-BF, F-LE', icon: 'ri-line-chart-line' },
              { label: 'Statistics', codes: 'S-ID', icon: 'ri-bar-chart-2-line' },
            ].map((s, i) => (
              <div key={i} className="bg-white/15 rounded-xl p-4">
                <i className={`${s.icon} text-amber-300 text-xl mb-2 block`}></i>
                <div className="font-bold text-sm">{s.label}</div>
                <div className="text-teal-200 text-xs mt-1">{s.codes}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Test Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose a Practice Test</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tests.map(test => (
            <div
              key={test.number}
              className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group"
            >
              {/* Card Header */}
              <div className={`bg-gradient-to-r ${test.color} p-6 text-white`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <i className={`${test.icon} text-2xl`}></i>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${test.difficultyColor}`}>
                    {test.difficulty}
                  </span>
                </div>
                <h3 className="text-xl font-black mb-1">{test.title}</h3>
                <p className="text-white/80 text-sm">{test.subtitle}</p>
              </div>

              {/* Card Body */}
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <i className="ri-question-line text-teal-500"></i>
                    <span>{test.questions.length} questions</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-time-line text-teal-500"></i>
                    <span>~45–60 min</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-line text-amber-500"></i>
                    <span>{test.questions.reduce((a, q) => a + q.points, 0)} pts</span>
                  </div>
                </div>

                <div className="mb-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Topics Covered</p>
                  <div className="flex flex-wrap gap-1.5">
                    {test.topics.map((topic, i) => (
                      <span key={i} className="px-2 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-medium whitespace-nowrap">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => setActiveTest({ number: test.number, questions: test.questions })}
                  className={`w-full py-3 bg-gradient-to-r ${test.color} text-white rounded-xl font-bold text-sm hover:opacity-90 transition-opacity shadow-md group-hover:shadow-lg whitespace-nowrap cursor-pointer`}
                >
                  <i className="ri-play-circle-fill mr-2"></i>
                  Start Practice Test {test.number}
                </button>
              </div>
            </div>
          ))}

          {/* Tips Card */}
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl border-2 border-amber-200 p-6 flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-lightbulb-fill text-amber-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-3">Regents Exam Tips</h3>
              <ul className="space-y-3">
                {[
                  'Read every question carefully — watch for key words like "at least," "not," and "which of the following."',
                  'Show all work on open-ended questions — partial credit is awarded.',
                  'Check your answers by substituting back into the original equation.',
                  'Use the quadratic formula when factoring is not obvious.',
                  'For systems, choose substitution when one variable is already isolated.',
                  'Manage your time: ~2 minutes per multiple-choice question.',
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 p-4 bg-amber-100 rounded-xl">
              <p className="text-xs text-amber-800 font-semibold text-center">
                <i className="ri-award-fill mr-1"></i>
                Passing score: 65 or above (raw score varies by year)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <i className="ri-graduation-cap-fill text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-white">Mr. Augustine's Math Academy</h3>
            </div>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              Empowering every student to master mathematics through proven teaching methods, personalized support, and rigorous practice.
            </p>
            <p className="text-xs text-gray-500 mt-4">© 2024 Mr. Augustine's Math Academy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
